<template>
    <b-overlay
        :show="status === 'loading' && !editMode"
        class="h-100"
    >
        <div class="card-header align-items-start">
            <div class="card-group-title">
                <h4>
                    {{ $t('org.dashboard.Daily Detailed Cost') }}
                </h4>
            </div>
            <b-card-actions-container
                v-if="!editMode && data.lineChartData.length && !viewTypeIsMobile"
                class="card-actions-container"
                :open="isVisible"
                @card:collapse="triggerCollapse"
            />
        </div>
        <CardDatesDetailFilterBox
            class="mb-2"
            :productList="productList"
            @save:filter="saveFilter"
        />
        <CardDatesDetailBarChart
            :datasets="datasets"
            :styles="fetchedChartResponsiveStyle"
            @click:barChart="handleBarClick"
        />
        <div
            ref="REF_COLLAPSE"
            :style="collapseResponsiveStyle"
        >
            <CardDatesTable
                v-show="!editMode && isVisible"
                :data="filterdTableData"
                @modal:showDetail="modalShowDetail"
            />
        </div>
    </b-overlay>
</template>
<script>
import { computed, watch, ref, toRefs, getCurrentInstance, nextTick } from '@vue/composition-api'
import CardDatesDetailBarChart from './CardDatesDetailBarChart'
import CardDatesDetailFilterBox from './CardDatesDetailFilterBox.vue'
import useDashboard from '@/composables/useDashboard'
import useDashboardRender from '@/composables/useDashboardRender'
import useSelectMonth from '@/composables/useSelectMonth'
import CardDatesTable from './CardDatesTable'
import BCardActionsContainer from '@/components/BCardActionsContainer'
export default {
    props: ['editMode', 'isOpen'],
    components: {
        CardDatesTable,
        CardDatesDetailFilterBox,
        CardDatesDetailBarChart,
        BCardActionsContainer
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const { isOpen, editMode } = toRefs(props)
        const isVisible = ref(isOpen.value)
        const store = vm.$store
        const { selectedMonth } = useSelectMonth()
        const { getSummaryDates } = useDashboard()
        const { viewType } = useDashboardRender()
        const viewTypeIsMobile = computed(() => viewType.value === 'mobile')
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const [status, data] = getSummaryDates(currentParams.value)
        const chartResponsiveStyle = ref({
            position: 'absolute',
            top: '110px',
            bottom: 0,
            left: '0',
            right: '0',
            'margin': '0rem 1.7rem 1.5rem 1.7rem'
        })

        const productList = computed(() => {
            return data.value.dates.reduce((acc, cur) => {
                cur.products.forEach(el => {
                    if (!acc.includes(el.productCode)) acc.push(el.productCode)
                })
                return acc
            }, [])
        })
        const chartFilter = ref(productList.value)
        const filterdTableData = computed(() => {
            return data.value.dates.map(item => {
                return {
                    ...item,
                    products: item.products.filter(subItem => chartFilter.value.includes(subItem.productCode))
                }
            })
        })
        const colors = [
            '#6966EF',
            '#6684EF',
            '#66A5EF',
            '#EF6666',
            '#EFA066',
            '#EF66D9',
            '#B366EF',
            '#66EFEF',
            '#66ADEF'
        ]
        const datasets = computed(() => {
            return Object.values(data.value.dates.reduce((acc, cur) => {
                cur.products.slice().reverse().forEach((item, index) => {
                    if ((editMode.value ? '2024-05' : selectedMonth.value) !== cur.date.substring(0, 7)) return acc
                    if (!(item.productCode in acc)) {
                        acc[item.productCode] = {
                            label: item.productCode,
                            backgroundColor: colors[(cur.products.length - index) % colors.length],
                            data: [],
                            stack: 'ProductCode'
                        }
                    }
                    let startIndex = acc[item.productCode].data.length + 1
                    let endIndex = +(cur.date.substring(8, 10))
                    for (let i = startIndex; i < endIndex; i++) {
                        acc[item.productCode].data.push(null)
                    }
                    acc[item.productCode].data.push({
                        x: cur.date,
                        y: item.cost
                    })
                })
                return acc
            }, {})).filter(item => (chartFilter.value).includes(item.label))
        })
        const collapseResponsiveStyle = {
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%'
        }
        const REF_COLLAPSE = ref(null)
        const triggerCollapse = (status) => {
            context.emit('action:showTable', { component: 'card-dates-detail', status })
        }
        const handleBarClick = ({ xLabel, productCode }) => {
            const item = data.value.dates.find(item => item.date.substring(8, 10) === xLabel) ?? {}
            if (!item.date) return
            modalShowDetail({ date: item.date, productCode })
        }
        const modalShowDetail = (params) => {
            context.emit('modal:showDetail', params)
        }
        const saveFilter = (filteredProductList) => {
            chartFilter.value = filteredProductList
        }
        watch(productList, (value, oldValue) => {
            chartFilter.value = value
        })
        watch(currentParams, (value, oldValue) => {
            getSummaryDates(value)
        }, {
            deep: true
        })
        watch(isOpen, (value, oldValue) => {
            isVisible.value = value
            nextTick(() => {
                const tableHeight = value ? vm.$refs['REF_COLLAPSE'].offsetHeight : 0
                chartResponsiveStyle.value.bottom = value ? `${tableHeight}px` : 0
            })
        })
        const fetchedChartResponsiveStyle = computed(() => chartResponsiveStyle.value)
        return {
            fetchedChartResponsiveStyle,
            datasets,
            isVisible,
            filterdTableData,
            data,
            productList,
            status,
            collapseResponsiveStyle,
            triggerCollapse,
            handleBarClick,
            modalShowDetail,
            viewTypeIsMobile,
            REF_COLLAPSE,
            saveFilter
        }
    }
}
</script>
